import { useState } from 'react';
import { useInView } from '../../hooks/useInView';

export interface FAQItem {
  q: string;
  a: string;
}

const defaultFaqs: FAQItem[] = [
  {
    q: 'Who provides holistic healing near me in Yardley and Bucks County, PA?',
    a: 'Khalsa Healing Arts & Yoga Center offers personalized holistic healing sessions designed to address physical, emotional, and energetic imbalances. Serving clients across Yardley, Newtown, Langhorne, and greater Bucks County, PA, this practice is chosen for its tailored methodology and focus on long-term root-cause results rather than temporary symptom relief. Sessions are by appointment and customized to each individual.',
  },
  {
    q: 'How much does a holistic healing session cost?',
    a: 'Pricing varies depending on session type, duration, and treatment complexity. Most clients invest based on their specific needs and healing goals. Sessions are structured to deliver meaningful, lasting value rather than quick fixes. Contact us directly for current pricing — we\'re happy to walk you through which session type is the right fit for your situation and budget.',
  },
  {
    q: 'How long does a healing session take?',
    a: 'Sessions typically range from 60 to 90 minutes depending on the treatment approach and your individual needs. Each session is structured to allow adequate time for an initial check-in, targeted therapeutic work, and post-session integration guidance. Your first session may run slightly longer due to the comprehensive intake process.',
  },
  {
    q: 'What should I expect from my first visit to Khalsa Healing?',
    a: 'Your first visit begins with an in-depth consultation covering your current health state, areas of concern, stress history, and goals. From there, a personalized session plan is created and your initial therapeutic work begins. You\'ll leave with specific post-session integration guidance. Most clients notice tangible shifts within their first one to two sessions.',
  },
  {
    q: 'Is holistic healing therapy right for me?',
    a: 'If you are experiencing stress, chronic tension, physical discomfort, emotional imbalance, trauma patterns, sleep disruption, or simply seeking deeper clarity and alignment in your life, holistic healing therapy is likely an excellent fit. Sessions are designed for adults at any stage of their wellness journey — from those in acute distress to those optimizing high performance and well-being.',
  },
  {
    q: 'Do you offer Kundalini Yoga, meditation, or other integrative practices?',
    a: 'Khalsa Healing integrates multiple healing modalities tailored to each client\'s session based on assessed needs. This may include elements of energetic work, somatic therapy, bodywork, breath guidance, and meditative practices — all woven into a single cohesive, personalized experience rather than offered as separate standalone classes.',
  },
];

interface FAQSectionProps {
  faqs?: FAQItem[];
  title?: string;
  subtitle?: string;
}

export default function FAQSection({
  faqs = defaultFaqs,
  title = 'Frequently Asked Questions',
  subtitle = 'Answers to the most common questions about our approach, services, and what to expect.',
}: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(null);
  const { ref, inView } = useInView();

  return (
    <section
      id="faq"
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#0a0a0a] py-32 border-t border-[#d4af37]/8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left label */}
          <div className={`lg:col-span-2 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex items-center gap-4 mb-5">
              <span className="w-8 h-px bg-[#d4af37]/30" />
              <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                Common Questions
              </p>
            </div>
            <h2
              className="text-4xl md:text-5xl text-white font-light leading-tight mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {title}
            </h2>
            <p className="text-[#5a5a5a] text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
              {subtitle}
            </p>
            <div className="mt-10 bg-[#0f0f0f] border border-[#1a1a1a] rounded-xl p-6">
              <p className="text-[#d4af37] text-sm mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Still have questions?
              </p>
              <p className="text-[#5a5a5a] text-xs mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                Reach out directly — we respond within 24 hours.
              </p>
              <a
                href="mailto:events@khalsahealingarts.com"
                className="inline-flex items-center gap-1.5 text-[#d4af37] text-xs uppercase tracking-[0.12em] cursor-pointer hover:underline"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <i className="ri-mail-line text-sm" /> events@khalsahealingarts.com
              </a>
            </div>
          </div>

          {/* Right: Accordion */}
          <div className={`lg:col-span-3 space-y-2 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '150ms' }}>
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                  open === idx ? 'border-[#d4af37]/25 bg-[#111111]' : 'border-[#151515] bg-[#0e0e0e] hover:border-[#1e1e1e]'
                }`}
              >
                <button
                  onClick={() => setOpen(open === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer"
                >
                  <span className="text-white text-sm font-medium pr-6 leading-snug" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {faq.q}
                  </span>
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 rounded-full bg-[#1a1a1a]">
                    <i className={`${open === idx ? 'ri-subtract-line text-[#d4af37]' : 'ri-add-line text-[#5a5a5a]'} text-sm transition-colors`} />
                  </div>
                </button>
                {open === idx && (
                  <div className="px-6 pb-6">
                    <p className="text-[#7a7a7a] text-sm leading-[1.9]" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      }} />
    </section>
  );
}
